// React to another tab signing this realm in or out.
//
// localStorage is shared across tabs but React state is not: a guard that
// checked storage once kept its answer until something ELSE re-rendered it,
// so a session ended in tab B left tab A running on a corpse until a request
// 401ed mid-click. The browser already broadcasts cross-tab storage writes —
// the `storage` event — this hook just turns "one of MY keys changed in
// another tab" into a re-render, so the guard re-reads reality immediately.
//
// (The event never fires in the tab that did the writing — that tab's own
// login/logout code navigates explicitly, so it needs no signal.)

import { useSyncExternalStore } from 'react';

let version = 0;
const listeners = new Set();
const watchedKeys = new Set();

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    // key === null means storage.clear() — treat as "anything changed".
    if (e.key !== null && !watchedKeys.has(e.key)) return;
    version += 1;
    listeners.forEach((l) => l());
  });
}

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => version;

/**
 * Returns a number that changes whenever another tab writes any of `keys`.
 * Use it for nothing except making the component re-evaluate storage.
 */
export default function useAuthPresence(keys) {
  keys.forEach((k) => watchedKeys.add(k));
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
